import { NetworkStatus, useReactiveVar } from "@apollo/client";
import { useCallback, useMemo } from "react";

import { useAvailableCustomMetrics } from "./useAvailableCustomMetrics";
import { useCustomMetricSortVariables } from "./useCustomMetricSortVariables";
import { useInfiniteScroll } from "./useInfiniteScroll";

import { filterValuesVar } from "@/features/player-filter";
import {
  useRosterManagementListNationalitiesQuery,
  usePlayerSearchFilterPlayersQuery,
} from "@/generated/graphql";
import { useCanAccessFeature } from "@/providers/FeatureFlags";
import { IFilterValueRange } from "@/stores/filterValues";
import { watchlistSortVar } from "@/stores/selectionSort";
import {
  getCustomMetricFilterFieldName,
  hasCustomMetricFilter,
} from "@/types/customMetrics";
import { groupNationalitiesByCountry } from "@/utils/nationalityCountryGroups";
import { useIsFocused } from "expo-router/react-navigation";

/**
 * Hook for filtering and sorting players with support for custom metrics.
 */

export const useFilter = () => {
  const isSortPlayerEnable = useCanAccessFeature("globalSort");
  const filterVars = useReactiveVar(filterValuesVar);
  const sortValues = useReactiveVar(watchlistSortVar);
  // The list only translates `custom_*` filter keys; with none there is
  // nothing to build, so the player profile skips a read that can take 20 s
  // on a cold replica (SE-8832).
  const { availableCustomMetrics } = useAvailableCustomMetrics({
    enabled: hasCustomMetricFilter(filterVars),
  });
  const isFilterScreenFocused = useIsFocused();

  /**
   * A selected nationality is one country, but the entity it was resolved
   * from may be just one of several `Nationality` rows naming that country
   * (its own row plus every dual-nationality combination naming it) — the
   * backend matches `nationalities` by exact name, so the selection must
   * expand to every one of those names to still return dual-national
   * players (SE-8325). Same `{first: 1000}` query useFilterState already
   * issues for the filter sheet, so this reads from Apollo's cache rather
   * than firing a second network request in the common case.
   */
  const { data: nationalitiesData } = useRosterManagementListNationalitiesQuery(
    {
      variables: { first: 1000 },
      skip: !filterVars?.nationalities?.length,
    }
  );
  const nationalityMemberNamesByLabel = useMemo(() => {
    const groups = groupNationalitiesByCountry(
      (nationalitiesData?.listNationalities?.edges ?? []).map(e => e.node)
    );
    return Object.fromEntries(
      groups.map(group => [group.label, group.memberNames])
    ) as Readonly<Record<string, readonly string[]>>;
  }, [nationalitiesData?.listNationalities?.edges]);

  // Get custom metric sort variables using shared hook
  const { sortVariables } = useCustomMetricSortVariables(
    sortValues,
    isSortPlayerEnable
  );

  // Build custom stats filter for backend
  const customStatsFilter = useMemo(() => {
    if (!filterVars || !availableCustomMetrics.length) return undefined;

    const customFilters = availableCustomMetrics
      .map(metric => {
        const filterKey = getCustomMetricFilterFieldName(metric.id);
        const filterRange = filterVars[filterKey as keyof typeof filterVars] as
          | IFilterValueRange
          | undefined;

        if (!filterRange) return null;

        // Check if filter is different from default (full) range
        const isFiltered =
          filterRange.min > metric.minValue ||
          filterRange.max < metric.maxValue;

        if (!isFiltered) return null;

        return {
          statName: metric.id,
          minValue: filterRange.min,
          maxValue: filterRange.max,
        };
      })
      .filter(
        (
          filter
        ): filter is { statName: string; minValue: number; maxValue: number } =>
          filter !== null
      );

    return customFilters.length > 0 ? customFilters : undefined;
  }, [filterVars, availableCustomMetrics]);

  const variables = useMemo(() => {
    return {
      filter: {
        leagueIds: filterVars?.leagues?.map?.(league => league.id) ?? undefined,
        competitionIds:
          filterVars?.competitions?.map?.(competition => competition.id) ??
          undefined,
        nationalities:
          filterVars?.nationalities?.flatMap?.(
            nationality =>
              nationalityMemberNamesByLabel[nationality.name] ?? [
                nationality.name,
              ]
          ) ?? undefined,
        positionIds:
          filterVars?.positions?.map?.(position => position.id) ?? undefined,
        teamIds: filterVars?.clubs?.map?.(club => club.id) ?? undefined,
        minAge: filterVars?.age?.min,
        maxAge: filterVars?.age?.max,
        minValuation: filterVars?.salary?.min,
        maxValuation: filterVars?.salary?.max,
        roleArchetypes: filterVars?.roles?.map?.(role => role.id) ?? undefined,
        minMonthsRemaining: filterVars?.monthsRemaining?.min,
        maxMonthsRemaining: filterVars?.monthsRemaining?.max,
        minFitScore: filterVars?.playerStyleFit?.min,
        maxFitScore: filterVars?.playerStyleFit?.max,
        minTeamStyleFit: filterVars?.teamStyleFit?.min,
        maxTeamStyleFit: filterVars?.teamStyleFit?.max,
        minGpr: filterVars?.gpr?.min,
        maxGpr: filterVars?.gpr?.max,
        minCarrying: filterVars?.carrying?.min,
        maxCarrying: filterVars?.carrying?.max,
        minDribbling: filterVars?.dribbling?.min,
        maxDribbling: filterVars?.dribbling?.max,
        minPassing: filterVars?.passing?.min,
        maxPassing: filterVars?.passing?.max,
        minShooting: filterVars?.shooting?.min,
        maxShooting: filterVars?.shooting?.max,
        minGkPassing: filterVars?.gkPassing?.min,
        maxGkPassing: filterVars?.gkPassing?.max,
        minGkPositioning: filterVars?.gkPositioning?.min,
        maxGkPositioning: filterVars?.gkPositioning?.max,
        minGkShotStopping: filterVars?.gkShotStopping?.min,
        maxGkShotStopping: filterVars?.gkShotStopping?.max,
        // Add custom metrics filtering
        customStats: customStatsFilter,
      },
      // Use shared sort variables from hook
      ...sortVariables,
    };
  }, [
    filterVars,
    sortVariables,
    customStatsFilter,
    nationalityMemberNamesByLabel,
  ]);

  const filterCount = Object.values(variables?.filter ?? {}).filter(
    value => value !== undefined
  ).length;

  // const [debouncedVariables] = useDebounce(variables, 300);

  const { data, loading, fetchMore, refetch, networkStatus } =
    usePlayerSearchFilterPlayersQuery({
      variables: variables,
      skip: !isFilterScreenFocused || filterCount === 0, // Only refetch if user is logged in and filters are applied. If no filters are applied, the generated query returns too many results and breaks the app.
      // skip: true,
      /**
       * PERFORMANCE: Using 'no-cache' to prevent Android touch delay.
       *
       * Problem: On Android, Apollo's cache normalization blocks the JS thread
       * for 3-7 seconds when processing large PlayerSearchFilterPlayers responses.
       * This causes severe touch input delays - buttons become unresponsive while
       * the cache is being updated.
       *
       * Root cause: Apollo Client normalizes every entity in the response into its
       * in-memory cache. For queries returning hundreds of players with nested data
       * (positions, leagues, stats), this normalization is computationally expensive.
       * Unlike iOS, Android's JS thread performance makes this blocking noticeable.
       *
       * Solution: 'no-cache' bypasses cache normalization entirely. The response
       * goes directly to the component without being written to or read from cache.
       *
       * Trade-offs:
       * - No automatic cache updates for these players (acceptable - filter results
       * are transient and don't need to persist)
       * - Each query always hits the network (acceptable - filters change frequently)
       * - Other components won't see these players in cache (acceptable - player
       * detail pages fetch their own data)
       * @see https://github.com/geminisportsai/frontend-v2/specs/slow-android.md
       */
      // fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  // const stringVariables = JSON.stringify(variables);

  const isRefreshing = networkStatus === NetworkStatus.refetch;
  const players = useMemo(() => {
    return data?.filterPlayers?.edges.map(edge => edge.node) ?? [];
  }, [data]);

  const { loadMore: handleLoadMore } = useInfiniteScroll({
    pageInfo: data?.filterPlayers?.pageInfo,
    fetchMore,
    networkStatus,
  });

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  return {
    data,
    loading,
    isRefreshing,
    players,
    handleLoadMore,
    handleRefresh,
  };
};
